import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

const TOKEN_KEY = "cargoscan_token";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appMode, setAppMode] = useState("personal");

  useEffect(() => {
    if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete api.defaults.headers.common.Authorization;
  }, [token]);

  const refresh = async () => {
    try {
      const [meRes, sRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/settings"),
      ]);
      setUser(meRes.data?.user || null);
      setAppMode(sRes.data?.app_mode || "personal");
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.mfa_required) {
      return { mfa_required: true, challenge_id: r.data.challenge_id, ttl_minutes: r.data.ttl_minutes };
    }
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return { user: r.data.user };
  };

  const verifyMfa = async (challenge_id, code) => {
    const r = await api.post("/auth/login/verify", { challenge_id, code });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
    setUser((u) => (u ? { ...u, must_change_password: false } : u));
  };

  const ssoStart = async () => {
    const r = await api.get("/auth/sso/start");
    sessionStorage.setItem("sso_state", r.data.state);
    window.location.href = r.data.authorize_url;
  };

  const ssoCallback = async (code, state) => {
    const r = await api.post("/auth/sso/callback", { code, state });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    delete api.defaults.headers.common.Authorization;
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, appMode, login, verifyMfa, changePassword, ssoStart, ssoCallback, logout, refresh, setAppMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
